import React from "react";
import GlowingFootball from "./decor/GlowingFootball";

// Layered cosmic background — used behind sections.
// Variants:
//   default  — subtle nebulas + starfield
//   intense  — adds lightning overlay + 2 drifting footballs
//   football — richer footballs at varied depths (primary identity backdrop)
//   rich     — maximum football density; used on tentpole narrative sections
const CosmicBackground = ({ variant = "default" }) => {
  const hasFootballs = ["football", "intense", "rich"].includes(variant);

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Starfield base */}
      <div className="absolute inset-0 starfield opacity-80" />

      {/* Drifting nebulas */}
      <div
        className="absolute -top-32 -left-24 h-[40rem] w-[40rem] rounded-full drift-slow opacity-50"
        style={{
          background:
            "radial-gradient(circle at center, rgba(255,45,92,0.45) 0%, rgba(255,45,92,0) 60%)",
          filter: "blur(40px)",
        }}
      />
      <div
        className="absolute top-1/3 -right-32 h-[36rem] w-[36rem] rounded-full drift-slow opacity-45"
        style={{
          background:
            "radial-gradient(circle at center, rgba(61,191,255,0.40) 0%, rgba(61,191,255,0) 60%)",
          filter: "blur(50px)",
          animationDelay: "-6s",
        }}
      />
      <div
        className="absolute bottom-0 left-1/4 h-[32rem] w-[32rem] rounded-full drift-slow opacity-50"
        style={{
          background:
            "radial-gradient(circle at center, rgba(155,77,255,0.45) 0%, rgba(155,77,255,0) 60%)",
          filter: "blur(50px)",
          animationDelay: "-12s",
        }}
      />
      <div
        className="absolute top-2/3 left-1/2 h-[28rem] w-[28rem] rounded-full drift-slow opacity-40"
        style={{
          background:
            "radial-gradient(circle at center, rgba(255,61,203,0.40) 0%, rgba(255,61,203,0) 60%)",
          filter: "blur(40px)",
          animationDelay: "-3s",
        }}
      />

      {/* Vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 30%, rgba(5,2,14,0.85) 100%)",
        }}
      />

      {hasFootballs && (
        <div className="absolute inset-0 lightning-overlay opacity-55" />
      )}

      {/* Drifting glowing footballs — recurring visual identity element */}
      {hasFootballs && (
        <>
          {/* Top-right anchor */}
          <GlowingFootball
            size={150}
            color="#FF2D5C"
            className="-top-10 right-[5%] opacity-55"
            speed={80}
            delay={0}
          />
          {/* Bottom-left anchor */}
          <GlowingFootball
            size={90}
            color="#3DBFFF"
            className="bottom-[10%] left-[3%] opacity-50"
            speed={110}
            delay={-12}
          />

          {variant !== "intense" && (
            <>
              {/* Mid-right floater */}
              <GlowingFootball
                size={70}
                color="#FF3DCB"
                className="top-[52%] right-[13%] opacity-45"
                speed={95}
                delay={-25}
              />
              {/* Small far-depth top-left */}
              <GlowingFootball
                size={48}
                color="#9B4DFF"
                className="top-[14%] left-[18%] opacity-35"
                speed={135}
                delay={-8}
                intensity={0.8}
              />
            </>
          )}

          {variant === "rich" && (
            <>
              {/* Mid-center deep */}
              <GlowingFootball
                size={110}
                color="#6FF6FF"
                className="top-[38%] left-[48%] opacity-30"
                speed={120}
                delay={-18}
                intensity={0.85}
              />
              {/* Small bottom-right */}
              <GlowingFootball
                size={55}
                color="#FF2D5C"
                className="bottom-[22%] right-[26%] opacity-40"
                speed={90}
                delay={-30}
                intensity={0.9}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CosmicBackground;
